import React, { useMemo } from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { useAuth } from '../../contexts/AuthContext';
import { AVAILABLE_POLICIES } from '../../config/policies';
import { isStellarNetwork } from '../../config/networks';
import { getColorForPolicyId, PASTEL_COLORS, PolicyPastelColor } from '../../utils/policyColors';
import './Step6Summary.css';

const Step6Summary: React.FC = () => {
  const { wizardData } = useWizard();
  const { clientId } = useAuth();
  const stellar = isStellarNetwork(wizardData.chain);

  const trustlineEnabled = useMemo(() => {
    return wizardData.methodMappings.some(m => m.policyId !== null);
  }, [wizardData.methodMappings]);

  const mappedPolicies = useMemo(() => {
    const usedIds = new Set(
      wizardData.methodMappings
        .map((m) => m.policyId)
        .filter((id): id is string => Boolean(id))
    );
    return wizardData.namedPolicies.filter((p) => usedIds.has(p.id));
  }, [wizardData.namedPolicies, wizardData.methodMappings]);

  // Create a map of policy IDs to colors
  const policyColorMap = useMemo(() => {
    const map = new Map<string, PolicyPastelColor>();
    mappedPolicies.forEach(policy => {
      map.set(policy.id, getColorForPolicyId(policy.id));
    });
    return map;
  }, [mappedPolicies]);

  return (
    <div className="wizard-step step6-summary">
      <h2 className="wizard-step-title">Summary</h2>
      <p className="wizard-step-description">
        Review all the information before submitting your Trustline configuration.
      </p>

      <div className="step6-summary-container">
        <div className="step6-summary-section">
          <h3 className="step6-section-title">Basic Information</h3>
          <div className="step6-summary-item">
            <span className="step6-label">dApp Name:</span>
            <span className="step6-value">{wizardData.dAppName || 'N/A'}</span>
          </div>
          <div className="step6-summary-item">
            <span className="step6-label">Trustline Account:</span>
            <span className="step6-value">{wizardData.email || 'N/A'}</span>
          </div>
          {clientId && (
            <div className="step6-summary-item">
              <span className="step6-label">Client ID:</span>
              <span className="step6-value">{clientId}</span>
            </div>
          )}
          <div className="step6-summary-item">
            <span className="step6-label">Chain:</span>
            <span className="step6-value">{wizardData.chain?.name || 'N/A'} (Chain ID: {wizardData.chain?.chainId || 'N/A'})</span>
          </div>
          <div className="step6-summary-item">
            <span className="step6-label">Contract Address:</span>
            <span className="step6-value step6-address">{wizardData.contractAddress || 'N/A'}</span>
          </div>
          {wizardData.isTrustlineFirewall && (
            <>
              <div className="step6-summary-item">
                <span className="step6-label">Contract type:</span>
                <span className="step6-value">Trustline Firewall</span>
              </div>
              <div className="step6-summary-item">
                <span className="step6-label">Protected target:</span>
                <span className="step6-value step6-address">{wizardData.firewallTargetAddress || 'N/A'}</span>
              </div>
            </>
          )}
          <div className="step6-summary-item">
            <span className="step6-label">
              {stellar ? 'Validation Engine:' : 'Validation Engine Proxy:'}
            </span>
            <span className="step6-value step6-address">{wizardData.validationEngineProxyAddress || 'N/A'}</span>
          </div>
          {!stellar && (
            <div className="step6-summary-item">
              <span className="step6-label">Validation Engine Implementation:</span>
              <span className="step6-value step6-address">{wizardData.validationEngineImplementationAddress || 'N/A'}</span>
            </div>
          )}
        </div>

        <div className="step6-summary-section">
          <h3 className="step6-section-title">Policies ({mappedPolicies.length})</h3>
          {mappedPolicies.length === 0 ? (
            <p className="step6-empty">No policies mapped to methods</p>
          ) : (
            <div className="step6-policies">
              {mappedPolicies.map((policy) => {
                const policyOption = AVAILABLE_POLICIES.find(p => p.id === policy.policyType);
                const color = policyColorMap.get(policy.id) || PASTEL_COLORS[0];
                return (
                  <div 
                    key={policy.id} 
                    className="step6-policy-item"
                    style={{
                      backgroundColor: color.bg,
                      borderColor: color.border,
                      borderLeftColor: color.text,
                      borderLeftWidth: '4px',
                      borderLeftStyle: 'solid',
                    }}
                  >
                    <div className="step6-policy-name" style={{ color: color.text }}>{policy.name}</div>
                    <div className="step6-policy-type">{policyOption?.name || policy.policyType}</div>
                    {policy.customizations.emails && policy.customizations.emails.length > 0 && (
                      <div className="step6-policy-emails">
                        {policy.customizations.requiredAdditionalAuthentications != null ||
                        policy.customizations.validatorEmails
                          ? 'Initiator'
                          : 'Allowed domains/addresses'}
                        : {policy.customizations.emails.join(', ')}
                      </div>
                    )}
                    {(policy.customizations.requiredAdditionalAuthentications != null ||
                      (policy.customizations.validatorEmails &&
                        policy.customizations.validatorEmails.length > 0)) && (
                      <div className="step6-policy-emails">
                        Validators ({policy.customizations.requiredAdditionalAuthentications ?? 0}{' '}
                        required):{' '}
                        {(policy.customizations.validatorEmails || []).join(', ') || 'any email'}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="step6-summary-section">
          <h3 className="step6-section-title">Method Mappings ({wizardData.methodMappings.length})</h3>
          {wizardData.methodMappings.length === 0 ? (
            <p className="step6-empty">No methods mapped</p>
          ) : (
            <div className="step6-mappings">
              {wizardData.methodMappings.map((mapping, index) => {
                const policy = wizardData.namedPolicies.find(p => p.id === mapping.policyId);
                const color = policy ? policyColorMap.get(policy.id) : null;
                return (
                  <div 
                    key={index} 
                    className={`step6-mapping-item ${policy ? 'step6-has-policy' : ''}`}
                    style={color ? {
                      backgroundColor: color.bg,
                      borderColor: color.border,
                      borderLeftColor: color.text,
                      borderLeftWidth: '4px',
                      borderLeftStyle: 'solid',
                    } : undefined}
                  >
                    <div className="step6-method-name">{mapping.methodName}</div>
                    <div 
                      className="step6-mapping-arrow"
                      style={color ? { color: color.text } : undefined}
                    >
                      →
                    </div>
                    <div 
                      className="step6-mapping-policy"
                      style={color ? { color: color.text } : undefined}
                    >
                      {policy ? policy.name : 'No policy (all transactions blocked)'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {wizardData.sanctionsEnabled !== null && (
          <div className="step6-summary-section">
            <h3 className="step6-section-title">On-Chain Configuration</h3>
            <div className="step6-summary-item">
              <span className="step6-label">Trustline validation:</span>
              <span className="step6-value">{trustlineEnabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            <div className="step6-summary-item">
              <span className="step6-label">OFAC sanctions check:</span>
              <span className="step6-value">{wizardData.sanctionsEnabled ? 'Enabled' : 'Disabled'}</span>
            </div>
            {wizardData.sanctionsEnabled && wizardData.sanctionsOracleKey && (
              <div className="step6-summary-item">
                <span className="step6-label">Sanctions oracle key:</span>
                <span className="step6-value">{wizardData.sanctionsOracleKey}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Step6Summary;

